// Enhanced NavbarComponent WITHOUT URL-based Language Switching
// This version uses only environment variables and cookies for language switching

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Navbar from "react-bootstrap/Navbar";
import UserWayAccessibilityMenu from "../UserWayAccessibilityMenu/index";
import { isMobileOrTablet } from "../Utils/index";
import { useGoogleTranslate } from "../GoogleTranslate";
import "./index.css";

// Get language from environment variable as default
const getAppLanguage = () => {
  const envLang = process.env.REACT_APP_LANG;
  return (envLang === 'en' || envLang === 'es') ? envLang : 'es';
};

export default function NavbarComponent({ Lodata, defaultLanguage }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [navbar, setNavbar] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const isMobileOrTabletDevice = isMobileOrTablet();
  const { translateTo, getCurrentLanguage: getGoogleTranslateLanguage } = useGoogleTranslate();

  // Get current language from environment only
  const [currentLanguage, setCurrentLanguage] = useState(getAppLanguage());

  // Initialize language on component mount
  useEffect(() => {
    const envLanguage = getAppLanguage();
 

    // Update state if different
    if (envLanguage !== currentLanguage) {
      setCurrentLanguage(envLanguage);
    }

    // Apply translation
    applyTranslation(envLanguage);
  }, []);

  // Function to apply translation
  const applyTranslation = (lang) => {
    if (lang === 'es') {
      // Set Spanish translation cookie
      document.cookie = `googtrans=/en/es; path=/; domain=${window.location.hostname};`;
      document.cookie = `googtrans=/en/es; path=/; domain=.${window.location.hostname};`;
    } else {
      // Clear translation cookies for English
      document.cookie = `googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${window.location.hostname};`;
      document.cookie = `googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=.${window.location.hostname};`;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Dynamic language change function WITHOUT URL routing
  const changeLanguage = (lang) => {
 
    setCurrentLanguage(lang);

    // Apply translation
    applyTranslation(lang);

    // Close dropdown
    setDropdownOpen(false);

    // Just reload the current page to apply translation
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  // Languages supported
  const languages = [
    { code: 'en', name: 'English', flag: 'US' },
    { code: 'es', name: 'Español', flag: 'ES' }
  ];

  // Navbar scroll behavior
  let prevScrollPos = window.scrollY;
  window.onscroll = function () {
    const currentScrollPos = window.scrollY;
    const menuNavbar = document.getElementById("menuNavbar");
    if (!menuNavbar) return;

    if (currentScrollPos === 0) {
      menuNavbar.classList.remove("navbarTransition");
      setNavbar(false);
    } else {
      if (prevScrollPos > currentScrollPos) {
        setNavbar(true);
      } else if (prevScrollPos < currentScrollPos) {
        if (!isMobileOrTabletDevice) {
          setNavbar(true);
        } else {
          setNavbar(false);
          menuNavbar.classList.add("navbarTransition");
        }
      }
    }
    prevScrollPos = currentScrollPos;
  };

  // Render flag component
  const renderFlag = (countryCode) => {
    if (countryCode === 'ES') {
      return (
        <img
          src={`${process.env.REACT_APP_CDN_PATH_THEWAYTOYOUROWNHOME}assets/spanish-logo.webp`}
          alt="Spanish logo"
          style={{ width: '24px', height: '24px', objectFit: 'contain' }}
        />
      );
    } else if (countryCode === 'US') {
      return (
        <img
          src={`${process.env.REACT_APP_CDN_PATH_THEWAYTOYOUROWNHOME}assets/english-logo.webp`}
          alt="English logo"
          style={{ width: '24px', height: '24px', objectFit: 'contain' }}
        />
      );
    }
    return null;
  };

  // Get current language display info
  const currentLangInfo = languages.find(lang => lang.code === currentLanguage) || languages[1];
  const otherLangInfo = languages.find(lang => lang.code !== currentLanguage) || languages[0];

  return (
    <>
      <div>
        <Navbar
          id="menuNavbar"
          collapseOnSelect
          expand="lg"
          fixed="top"
          bg="dark"
          variant="dark"
          className={navbar ? "navbar active" : "navbar"}
        >
          <Container className="navcont">
            <Navbar.Brand className="ps-3">
              <div className="nav-logo">
                <div>
                  <a
                    href="https://www.swmc.com/"
                     title="SWMC-Logo"
                    target="_blank"
                  >
                    <img
                      src="https://resources.swmc.com/swmc-images/sunwest-trust-white-logo.png"
                      className="main_logo"
                      alt="SWMC logo"
                    />
                  </a>
                </div>
              </div>
            </Navbar.Brand>

            <div style={{ display: 'flex', alignItems: 'center' }}>

              <a
                href={
                  currentLanguage === 'en'
                    ? 'https://www.elcaminoatupropiohogar.com/'
                    : 'https://www.thewaytoyourownhome.com/'
                }
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#EBF2FF80',
                  borderRadius: '20px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  width: '120px',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                  justifyContent: 'space-between',
                  textDecoration: 'none'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    marginRight: '8px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {renderFlag(currentLanguage === 'en' ? 'ES' : 'US')}
                  </div>
                  <span
                    style={{ color: '#000', fontSize: '14px', fontWeight: '500' }}
                    className="notranslate"
                    translate="no"
                  >
                    {currentLanguage === 'en' ? 'Español' : 'English'}
                  </span>
                </div>
              </a>

              <div className="desktop-AccessibilityMenu-show">
                <UserWayAccessibilityMenu color="white" width={30} height={30} />
              </div>
            </div>
          </Container>
        </Navbar>
      </div>
    </>
  );
}